import Icon from "@Components/generic/icon";
import { faChevronLeft, faChevronRight, faClose } from "@fortawesome/pro-solid-svg-icons";
import { Portal, Stack, styled, Typography } from "@mui/material";
import { DialogProps } from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { StyledDialog, StyledDialogActions, StyledDialogButton, StyledDialogTitle } from "./styled-components";

/**
 * This component is part of the VR app V2 redesign
 */

export type DialogSizeVariant = "wide" | "tall" | "mobile";

interface IDialogLayout {
  variant?: DialogSizeVariant;
}

export const BybVrDialogLayout = styled(Stack, {
  shouldForwardProp: prop => prop !== "variant",
})<IDialogLayout>(({ theme, variant = "wide" }) => ({
  border: `1px solid ${theme.palette.dsSurface.v300}`,
  borderRadius: theme.spacing(2.5),
  maxHeight: "90vh",
}));

export const BybVrDialogHeader = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  textAlign: "center",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(2),
}));

export const BybVrDialogSidebar = styled(Stack)(({ theme }) => ({
  width: 240,
  borderRight: `1px solid ${theme.palette.dsSurface.v100}`,
  overflow: "auto",
}));

export const BybVrDialogContent = styled(Stack)(({ theme }) => ({
  overflow: "hidden",
  flex: 1,
  minHeight: 0,
}));

export const BybVrDialogFooter = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(5),
}));

interface IBybVrDialog {
  name: string;
  title?: React.ReactNode;
  transparent?: boolean;
  dialogActions?: React.ReactNode;
  showCloseButton?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  width?: string;
}

const BybVrDialog = ({
  name,
  title,
  children,
  transparent,
  dialogActions,
  showCloseButton = false,
  onPrevious,
  onNext,
  canGoPrevious,
  canGoNext,
  width,
  ...props
}: IBybVrDialog & DialogProps) => {
  const hasNavigation = (onPrevious || onNext) && canGoPrevious !== undefined && canGoNext !== undefined;

  return (
    <>
      <StyledDialog aria-labelledby={`${name}-dialog-title`} {...props} transparent={transparent} width={width}>
        {title && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <StyledDialogTitle id={`${name}-dialog-title`}>
              {typeof title === "string" ? (
                <Typography variant="text2xl700" color="dsBase.v200">
                  {title}
                </Typography>
              ) : (
                title
              )}
            </StyledDialogTitle>
          </Stack>
        )}
        <DialogContent sx={theme => ({ overflow: "visible", padding: theme.spacing(2) })}>{children}</DialogContent>
        {dialogActions && <StyledDialogActions>{dialogActions}</StyledDialogActions>}
      </StyledDialog>
      {props.open && (
        <Portal>
          {hasNavigation && onPrevious && (
            <StyledDialogButton
              aria-label="Previous"
              onClick={onPrevious}
              disabled={!canGoPrevious}
              sx={{
                left: "8%",
                top: "50%",
                transform: "translate(-100%, -50%)",
                marginLeft: -4,
                "&:hover": {
                  transform: "translate(-100%, -50%) scale(1.05)",
                },
              }}
            >
              <Icon icon={faChevronLeft} size="sm" />
            </StyledDialogButton>
          )}
          {hasNavigation && onNext && (
            <StyledDialogButton
              aria-label="Next"
              onClick={onNext}
              disabled={!canGoNext}
              sx={{
                right: "8%",
                top: "50%",
                transform: "translate(100%, -50%)",
                marginRight: -4,
                "&:hover": {
                  transform: "translate(100%, -50%) scale(1.05)",
                },
              }}
            >
              <Icon icon={faChevronRight} size="sm" />
            </StyledDialogButton>
          )}
          {showCloseButton && (
            <StyledDialogButton
              aria-label="close"
              onClick={() => props.onClose?.({}, "escapeKeyDown")}
              sx={{
                right: "8%",
                top: "5%",
                transform: "translate(100%, -50%)",
                marginRight: -4,
                "&:hover": {
                  transform: "translate(100%, -50%) scale(1.05)",
                },
              }}
            >
              <Icon icon={faClose} size="sm" />
            </StyledDialogButton>
          )}
        </Portal>
      )}
    </>
  );
};

export default BybVrDialog;
